import {
  InternalOpsChannel,
  useInternalOpsChannels,
} from "@/lib/api/internal-ops-messaging";
import { useCurrentUser, useGetUserSupervisorAndChannel } from "@/lib/api/user";
import { useSignedMedia } from "@/lib/hooks/use-signed-media";
import { PostHogEvents, useTrackEvent } from "@/lib/posthog-events";
import { cn, getInitials } from "@/lib/utils";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { GroupChannel } from "@sendbird/chat/groupChannel";
import useSendbirdStateContext from "@sendbird/uikit-react/useSendbirdStateContext";
import { Moodies } from "@somethings-ui/core/assets";
import { useMemo } from "react";
import { ChatListSection } from "./chat-list-section";
import { isMentorIdentity } from "@/lib/utils/system-roles";

export type SupportChannelSelection = {
  sdkUrl: string;
  internalChannelId: string | null;
};

interface SupportChannelListProps {
  channels: GroupChannel[];
  loading: boolean;
  currentChannelUrl: string | null;
  onSelectChannel: (selection: SupportChannelSelection) => void;
}

export function SupportChannelList({
  channels,
  loading,
  currentChannelUrl,
  onSelectChannel,
}: SupportChannelListProps) {
  const { track } = useTrackEvent();
  const sb = useSendbirdStateContext();
  const myUserId = sb.config.userId;
  const { data: internalChannels } = useInternalOpsChannels();
  const { data: currentUser } = useCurrentUser();

  const systemRoles = currentUser?.systemRoles ?? [];
  const isCurrentUserMentor = isMentorIdentity(systemRoles);

  const { data: supervisorData } = useGetUserSupervisorAndChannel(
    currentUser?.id ?? "",
    !!currentUser?.id && isCurrentUserMentor,
  );
  const activeSupervisorId = supervisorData?.supervisor?.id ?? null;

  const internalByUrl = useMemo(
    () => new Map((internalChannels ?? []).map((c) => [c.sdk_url, c])),
    [internalChannels],
  );

  const buckets = useMemo(() => {
    const list = channels ?? [];
    const mentors: GroupChannel[] = [];
    const leadMentors: GroupChannel[] = [];
    const supervisors: GroupChannel[] = [];
    const admins: GroupChannel[] = [];
    const cohorts: GroupChannel[] = [];
    const groups: GroupChannel[] = [];
    const other: GroupChannel[] = [];

    for (const channel of list) {
      if (!channel.customType?.startsWith("INTERNAL_")) {
        continue;
      }
      const internal = internalByUrl.get(channel.url);
      if (internal?.kind === "cohort") {
        cohorts.push(channel);
      } else if (internal?.kind === "group") {
        groups.push(channel);
      } else if (internal?.kind === "direct") {
        const otherMember = internal.members?.find(
          (m) => m.user_id !== myUserId,
        );
        const role = otherMember?.user?.role?.toLowerCase();
        const isSupervisorUser =
          !!otherMember?.user?.system_roles?.includes("supervisor");
        const isMyActiveSupervisor =
          isCurrentUserMentor &&
          activeSupervisorId !== null &&
          otherMember?.user_id === activeSupervisorId;

        if (otherMember?.user?.deactivated) {
          other.push(channel);
        } else if (isMyActiveSupervisor) {
          supervisors.push(channel);
        } else if (isCurrentUserMentor && isSupervisorUser) {
          other.push(channel);
        } else if (isSupervisorUser) {
          supervisors.push(channel);
        } else if (role === "mentor") mentors.push(channel);
        else if (role === "lead_mentor") leadMentors.push(channel);
        else if (role === "admin" || role === "superadmin")
          admins.push(channel);
        else other.push(channel);
      } else {
        other.push(channel);
      }
    }

    return {
      mentors,
      leadMentors,
      supervisors,
      admins,
      cohorts,
      groups,
      other,
    };
  }, [
    channels,
    internalByUrl,
    myUserId,
    isCurrentUserMentor,
    activeSupervisorId,
  ]);

  const renderRow = (channel: GroupChannel) => {
    const internal = internalByUrl.get(channel.url) ?? null;
    return (
      <ChannelRow
        key={channel.url}
        channel={channel}
        internal={internal}
        isActive={currentChannelUrl === channel.url}
        onSelect={() =>
          onSelectChannel({
            sdkUrl: channel.url,
            internalChannelId: internal?.id ?? null,
          })
        }
      />
    );
  };

  const onCategoryToggle = (category: string, count: number) =>
    track(PostHogEvents.OPS_CHAT_CATEGORY_EXPAND, { category, count });

  const unreadCount = (list: GroupChannel[]) =>
    list.filter((c) => c.unreadMessageCount > 0).length;

  return (
    <div>
      {buckets.mentors.length > 0 && (
        <ChatListSection
          title="Mentors"
          badgeCount={unreadCount(buckets.mentors)}
          defaultOpen
          loading={loading}
          onToggle={(open) =>
            open && onCategoryToggle("mentors", buckets.mentors.length)
          }
        >
          {buckets.mentors.map(renderRow)}
        </ChatListSection>
      )}
      {buckets.leadMentors.length > 0 && (
        <ChatListSection
          title="Lead mentors"
          badgeCount={unreadCount(buckets.leadMentors)}
          defaultOpen
          loading={loading}
          onToggle={(open) =>
            open && onCategoryToggle("lead_mentors", buckets.leadMentors.length)
          }
        >
          {buckets.leadMentors.map(renderRow)}
        </ChatListSection>
      )}
      {buckets.supervisors.length > 0 && (
        <ChatListSection
          title="Supervisors"
          badgeCount={unreadCount(buckets.supervisors)}
          loading={loading}
          onToggle={(open) =>
            open && onCategoryToggle("supervisors", buckets.supervisors.length)
          }
        >
          {buckets.supervisors.map(renderRow)}
        </ChatListSection>
      )}
      {buckets.admins.length > 0 && (
        <ChatListSection
          title="Admins"
          badgeCount={unreadCount(buckets.admins)}
          loading={loading}
          onToggle={(open) =>
            open && onCategoryToggle("admins", buckets.admins.length)
          }
        >
          {buckets.admins.map(renderRow)}
        </ChatListSection>
      )}
      {buckets.cohorts.length > 0 && (
        <ChatListSection
          title="Cohorts"
          badgeCount={unreadCount(buckets.cohorts)}
          loading={loading}
          onToggle={(open) =>
            open && onCategoryToggle("cohorts", buckets.cohorts.length)
          }
        >
          {buckets.cohorts.map(renderRow)}
        </ChatListSection>
      )}
      {buckets.groups.length > 0 && (
        <ChatListSection
          title="Groups"
          badgeCount={unreadCount(buckets.groups)}
          loading={loading}
          onToggle={(open) =>
            open && onCategoryToggle("groups", buckets.groups.length)
          }
        >
          {buckets.groups.map(renderRow)}
        </ChatListSection>
      )}
      {buckets.other.length > 0 && (
        <ChatListSection
          title="Other"
          badgeCount={unreadCount(buckets.other)}
          loading={loading}
          onToggle={(open) =>
            open && onCategoryToggle("other", buckets.other.length)
          }
        >
          {buckets.other.map(renderRow)}
        </ChatListSection>
      )}
    </div>
  );
}

function ChannelRow({
  channel,
  internal,
  isActive,
  onSelect,
}: {
  channel: GroupChannel;
  internal: InternalOpsChannel | null;
  isActive: boolean;
  onSelect: () => void;
}) {
  const sb = useSendbirdStateContext();
  const myUserId = sb.config.userId;
  const { track } = useTrackEvent();

  const otherMembers = channel.members.filter((m) => m.userId !== myUserId);
  const isNamedChannel =
    !!internal && internal.kind !== "direct" && !!internal.channel_display_name;
  const title = isNamedChannel
    ? (internal!.channel_display_name as string)
    : otherMembers.map((m) => m.nickname || m.userId).join(", ") ||
      channel.name ||
      "Untitled";

  const lastMessage = channel.lastMessage as
    | { message?: string; url?: string; createdAt?: number }
    | undefined
    | null;
  const lastText =
    lastMessage?.message ?? (lastMessage?.url ? "[file]" : "No messages");

  const lastTime = lastMessage?.createdAt
    ? new Date(lastMessage.createdAt).toLocaleString("en-US", {
        hour: "numeric",
        minute: "numeric",
        hour12: true,
      })
    : "";

  const primaryMember = otherMembers[0];
  const avatarName = primaryMember?.nickname || primaryMember?.userId || title;
  const { url: avatarUrl } = useSignedMedia(
    primaryMember?.profileUrl ?? undefined,
  );

  return (
    <button
      type="button"
      onClick={() => {
        track(PostHogEvents.OPS_CHAT_THREAD_OPEN, {
          thread_id: channel.url,
          thread_type: otherMembers.length === 1 ? "user" : "group",
        });
        onSelect();
      }}
      className={cn(
        "flex w-full cursor-pointer flex-row items-center gap-2 px-4 py-3 text-left hover:bg-zinc-50",
        isActive && "bg-indigo-100 hover:bg-indigo-100",
      )}
    >
      <div
        className={cn(
          "size-2 shrink-0 rounded-full",
          channel.unreadMessageCount > 0 ? "bg-blue-600" : "bg-transparent",
        )}
      />
      <div className="flex size-11 shrink-0 items-center justify-center">
        {internal?.kind === "cohort" ? (
          <img
            src={Moodies.happy}
            alt="Cohort"
            className="size-10 object-contain"
          />
        ) : (
          <Avatar className="size-10 shrink-0">
            <AvatarImage src={avatarUrl} alt={avatarName} />
            <AvatarFallback className="bg-zinc-200 text-sm text-zinc-500">
              {getInitials(avatarName)}
            </AvatarFallback>
          </Avatar>
        )}
      </div>
      <div className="flex min-w-0 flex-1 flex-col gap-1">
        <div className="flex items-center justify-between gap-2">
          <span className="truncate text-sm font-bold text-black">{title}</span>
          <span className="shrink-0 text-xs text-[#a1a1aa]">{lastTime}</span>
        </div>
        <span className="truncate text-xs text-[#78716c]">{lastText}</span>
      </div>
    </button>
  );
}

// Mark unused props as intentional so TS doesn't complain on the export shape.
export type { InternalOpsChannel };
